import React, { useState, useEffect } from 'react';
import { useApi } from '../../../hooks/useApi';
import logger from "../../../utils/logger";
import { fmtDateTime } from '../../../utils/ruDates';

function AdminLogs() {
  const {
    request,
    showStatus
  } = useApi();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [expandedLogId, setExpandedLogId] = useState(null);
  const [filters, setFilters] = useState({
    user_id: '',
    action: '',
    entity_name: '',
    search: ''
  });
  const limit = 50;

  useEffect(() => {
    loadLogs();
  }, [page, filters.action, filters.entity_name]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit,
        offset: (page - 1) * limit,
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
      });
      const data = await request(`/api/v1/admin/logs?${params.toString()}`);
      setLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch (error) {
      logger.error('Failed to load logs', error);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = e => {
    e.preventDefault();
    setPage(1);
    loadLogs();
  };

  const downloadCSV = () => {
    if (logs.length === 0) {
      alert('Нет логов для скачивания');
      return;
    }

    const headers = ['Дата', 'Исполнитель', 'Email', 'Действие', 'Сущность', 'Entity ID', 'Детали'];
    const rows = logs.map(log => [
      log.created_at ? fmtDateTime(log.created_at) : '',
      log.user_name || log.user_email || 'System',
      log.user_email || '',
      log.action,
      log.entity_name,
      log.entity_id,
      log.details ? JSON.stringify(log.details) : ''
    ]);

    const csv = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `logs-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
  };

  return <div className="admin-page">
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px'
    }}>
      <h1>Логи системы</h1>
      <div style={{
        display: 'flex',
        gap: '10px'
      }}>
        <button onClick={() => {
          setPage(1);
          loadLogs();
        }} disabled={loading} style={{
          padding: '8px 16px',
          borderRadius: '4px',
          border: '1px solid #ddd',
          background: 'white',
          cursor: 'pointer'
        }}>
          Обновить
        </button>
        <button onClick={downloadCSV} disabled={loading || logs.length === 0} style={{
          padding: '8px 16px',
          borderRadius: '4px',
          border: '1px solid #27ae60',
          background: '#27ae60',
          color: 'white',
          cursor: 'pointer'
        }}>
          ⬇ Скачать CSV
        </button>
      </div>
    </div>

    {/* Filters */}
    <form onSubmit={handleSearch} style={{
      display: 'flex',
      gap: '10px',
      marginBottom: '20px',
      flexWrap: 'wrap',
      background: 'white',
      padding: '15px',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
    }}>
      <input placeholder="Поиск..." value={filters.search} onChange={e => setFilters({
        ...filters,
        search: e.target.value
      })} style={{
        flex: 1,
        padding: '8px',
        borderRadius: '4px',
        border: '1px solid #ddd'
      }} />

      <select value={filters.action} onChange={e => setFilters({
        ...filters,
        action: e.target.value
      })} style={{
        padding: '8px',
        borderRadius: '4px',
        border: '1px solid #ddd'
      }}>
        <option value="">Все действия</option>
        <option value="feature_used">Использование инструмента</option>
        <option value="feature_denied">Отказ в доступе</option>
        <option value="create">Создание</option>
        <option value="update">Обновление</option>
        <option value="delete">Удаление</option>
        <option value="login">Вход</option>
      </select>
      <input placeholder="Сущность (user, company...)" value={filters.entity_name} onChange={e => setFilters({
        ...filters,
        entity_name: e.target.value
      })} style={{
        padding: '8px',
        borderRadius: '4px',
        border: '1px solid #ddd'
      }} />

      <button type="submit" style={{
        padding: '8px 20px',
        background: '#3498db',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer'
      }}>Найти</button>
    </form>

    <div className="admin-table-container" style={{
      background: 'white',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      overflow: 'hidden'
    }}>
      <table style={{
        width: '100%',
        borderCollapse: 'collapse'
      }}>
        <thead style={{
          background: '#f8f9fa',
          borderBottom: '2px solid #e9ecef'
        }}>
          <tr>
            <th style={{
              padding: '12px',
              textAlign: 'left'
            }}>Дата</th>
            <th style={{
              padding: '12px',
              textAlign: 'left'
            }}>Исполнитель</th>
            <th style={{
              padding: '12px',
              textAlign: 'left'
            }}>Действие</th>
            <th style={{
              padding: '12px',
              textAlign: 'left'
            }}>Сущность</th>
            <th style={{
              padding: '12px',
              textAlign: 'left'
            }}>Детали</th>
          </tr>
        </thead>
        <tbody>
          {loading && <tr><td colSpan="5" style={{
            padding: '20px',
            textAlign: 'center'
          }}>Загрузка...</td></tr>}
          {!loading && logs.length === 0 && <tr><td colSpan="5" style={{
            padding: '20px',
            textAlign: 'center'
          }}>Логи не найдены</td></tr>}
          {logs.map(log => (
            <React.Fragment key={log.id}>
              <tr style={{
                borderBottom: '1px solid #e9ecef',
                fontSize: '13px'
              }}>
                <td style={{
                  padding: '12px',
                  whiteSpace: 'nowrap'
                }}>
                  {fmtDateTime(log.created_at)}
                </td>
                <td style={{
                  padding: '12px'
                }}>
                  <div style={{
                    fontWeight: '500'
                  }}>{log.user_email || 'System'}</div>
                  <div style={{
                    fontSize: '12px',
                    color: '#999'
                  }}>{log.user_id?.slice(0, 8)}</div>
                </td>
                <td style={{
                  padding: '12px'
                }}>
                  <span style={{
                    padding: '2px 6px',
                    borderRadius: '4px',
                    background: '#eee',
                    fontSize: '12px'
                  }}>{log.action}</span>
                </td>
                <td style={{
                  padding: '12px'
                }}>
                  <div style={{
                    fontWeight: '500'
                  }}>{log.entity_name}</div>
                  <div style={{
                    fontSize: '12px',
                    color: '#999'
                  }}>{log.entity_id?.slice(0, 8)}</div>
                </td>
                <td style={{
                  padding: '12px',
                  cursor: 'pointer',
                  color: '#3498db',
                  textDecoration: 'underline',
                  fontSize: '12px'
                }} onClick={() => setExpandedLogId(expandedLogId === log.id ? null : log.id)}>
                  {expandedLogId === log.id ? '▼ Скрыть' : '▶ Показать'}
                </td>
              </tr>
              {expandedLogId === log.id && (
                <tr style={{
                  borderBottom: '1px solid #e9ecef',
                  background: '#f8f9fa'
                }}>
                  <td colSpan="5" style={{
                    padding: '12px',
                    fontSize: '12px'
                  }}>
                    <div style={{
                      fontWeight: 'bold',
                      marginBottom: '8px',
                      color: '#333'
                    }}>Полные детали:</div>
                    <pre style={{
                      background: '#fff',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      padding: '8px',
                      overflow: 'auto',
                      maxHeight: '300px',
                      fontSize: '12px',
                      margin: '0'
                    }}>
                      {log.details ? JSON.stringify(log.details, null, 2) : 'Нет деталей'}
                    </pre>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>

    {/* Pagination */}
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      gap: '10px',
      marginTop: '20px',
      paddingBottom: '40px'
    }}>
      <button disabled={page <= 1} onClick={() => setPage(page - 1)} style={pageBtnStyle}>Назад</button>
      <span style={{
        alignSelf: 'center',
        fontWeight: 'bold'
      }}>Страница {page} / {Math.ceil(total / limit) || 1}</span>
      <button disabled={page * limit >= total} onClick={() => setPage(page + 1)} style={pageBtnStyle}>Вперед</button>
    </div>
  </div>;
}

const pageBtnStyle = {
  padding: '8px 16px',
  background: 'white',
  border: '1px solid #ddd',
  borderRadius: '4px',
  cursor: 'pointer'
};

export default AdminLogs;
